/* eslint-disable max-len */
/* eslint-disable react/jsx-props-no-spreading */
import {
  Box,
  Checkbox,
  Grid,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  makeStyles,
  Typography,
} from '@material-ui/core';
import React, {
  useContext, useState, useEffect,
} from 'react';
import { useHistory, useRouteMatch, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ExpandMore, ExpandLess } from '@material-ui/icons';
import AddIcon from '@material-ui/icons/Add';
import PageBanner from '../../components/utils/PageBanner';
import AssetManagementFiltersBar from '../../components/FilterBar';
// Local imports
import { useProject } from '../../config/ProjectContext';
import SelectedObjectsToDeployContext from '../../config/SelectedAssetsContext';
import AssetDetail from '../../components/AssetList/AssetDetail';
import GraphqlService from '../../service/graphqlService';
import YesNoDialog from '../../components/utils/YesNoDialog';
import {
  CREATE_DEVICE_DEPLOY_PERMISSION,
  DELETE_ASSETS_PERMISSION,
  NEW_ASSET_PATH,
} from '../../utils/constants';
import { ReactComponent as UploadLogo } from '../../assets/ic_file_upload.svg';
import MessageCard from '../../components/utils/MessageCard';
import { ReactComponent as DeleteIcon } from '../../assets/ic_bin_white.svg';
import ProgressDialog from '../../components/utils/ProgressDialog';
import PaginationComponent from '../../components/Pagination';
import { useSnackbar } from '../../providers/SnackbarContext';
import { formatToPrintableDate } from '../../utils/dateUtils';
import { StyledTableCell, StyledTableRow } from '../../components/utils/StyledTable';
import JsonPopup from '../../components/JsonPopup';
import useAnalytics from '../../hooks/useAnalytics';
import useRegionFeatureGate from '../../hooks/useRegionFeatureGate';

export default function AssetList() {
  const useStyles = makeStyles((theme) => ({
    pagination: {
      background: theme.palette.background.paper,
      borderRadius: 12,
      boxShadow: theme.customShadows.regularShadow,
      color: theme.palette.primary.text,
      float: 'right',
      padding: 0,
      width: '100%',
    },
    background: {
      backgroundColor: theme.palette.background.default,
      padding: 16,
    },
    button: {
      float: 'right',
      margin: '0 0 16px 24px',
    },
    deployButton: {
      float: 'left',
      margin: '0 0 16px 24px',
    },
    pageSizeTitle: {
      fontSize: 14,
      paddingRight: '24px',
    },
    paginationRoot: {
      display: 'flex',
      justifyContent: 'flex-end',
      width: '100%',
      padding: theme.spacing(2),
    },
    selectPaginationSize: {
      marginRight: '8px',
      borderRadius: 12,
    },
    paginationSizeLabel: {
      paddingRight: '12px',
      fontSize: 16,
    },
    tableIcons: {
      display: 'flex',
      justifyContent: 'flex-end',
      alignItems: 'center',
      paddingBottom: 16,
      flexWrap: 'wrap',
    },
    filtersContainer: {
      display: 'flex',
      justifyContent: 'flex-end',
      alignItems: 'flex-end',
      padding: theme.spacing(3),
      gap: theme.spacing(2),
      backgroundColor: theme.palette.background.paper,
      borderRadius: 12,
      marginBottom: theme.spacing(2),
      boxShadow: theme.customShadows.regularShadow,
    },
    tableButton: {
      width: '100%',
      height: 48,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    refreshButton: {
      height: 48,
      justifyContent: 'center',
      margin: 4,
      color: '#fff',
      borderRadius: 12,
    },
    assetTypeFilterStyle: {
      minWidth: 210,
    },
    descriptionFilterStyle: {
      minWidth: 310,
      marginRight: 4,
      selfAlign: 'center',
    },
    orderDateStyle: {
      minWidth: 130,
    },
    table: {
      borderRadius: 12,
      boxShadow: theme.customShadows.noTopShadow,
    },
    selectedItems: {
      fontSize: 14,
      color: theme.palette.text.secondary,
      marginBottom: 2,
    },
    filters: {
      display: 'flex',
      justifyContent: 'flex-end',
      marginBottom: theme.spacing(2),
      alignItems: 'flex-end',
    },
    descriptionFormControl: {
      minWidth: 250,
    },
  }));

  // STATE________________________________

  //  state values;
  const { project } = useProject();

  const [yesNoDialogOpen, setYesNoDialogOpen] = useState(false);
  const [yesNoDialogText, setYesNoDialogText] = useState('');
  const { selectedObjectsToDeploy: selectedAssets, setSelectedObjectsToDeploy: setSelectedAssets } = useContext(SelectedObjectsToDeployContext);
  const [sortDirection, setSortDirection] = useState('DESC');
  const [selectedAssetTypes, setSelectedAssetTypes] = useState([]);
  const [assetTypeFilter, setAssetTypeFilter] = useState();
  const [filterByAssetDescriptionValue, setFilterByAssetDescriptionValue] = useState('');
  const [_debouncedFilterAssetDescription, setDebouncedFilterAssetDescription] = useState('');
  const [isSearchTriggered, setIsSearchTriggered] = useState(false);
  const [hasInitializedSearch, setHasInitializedSearch] = useState(false);

  const [lastAssetTypeFilter, setLastAssetTypeFilter] = useState();
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const history = useHistory();
  const match = useRouteMatch();
  const { fileId: fileIdParam } = useParams();
  const [lastProjectCode, setLastProjectCode] = useState(null);

  const [expandedRow, setExpandedRow] = useState(null);

  const openSnackBar = useSnackbar();

  const { setAnalytics } = useAnalytics();

  const isFeatureAvailable = useRegionFeatureGate();

  const getType = assetTypeFilter === null ? null : assetTypeFilter;

  const fetchAssets = async () => {
    if (!isSearchTriggered) return { data: [], pagination: null };
    
    if (project?.code !== lastProjectCode) {
      setCurrentPage(0);
      setLastProjectCode(project?.code);
      setSelectedAssetTypes([]);
      setAssetTypeFilter(null);
      setSelectedAssets([]);
    } else if (assetTypeFilter !== lastAssetTypeFilter) {
      setCurrentPage(0);
      setLastAssetTypeFilter(assetTypeFilter);
      setSelectedAssets([]);
    }

    return GraphqlService.listAssetsNotDeleted({
      projectId: project?.code,
      page: currentPage,
      pageSize,
      sortDirection,
      type: getType,
      assetDescriptionValue: _debouncedFilterAssetDescription || undefined,
      assetDescriptionKey: _debouncedFilterAssetDescription ? 'Version' : undefined,
    });
  };

  const getAsset = async () => GraphqlService.getAsset({
    projectId: project?.code,
    fileId: fileIdParam,
  });

  const {
    data: assetFromParams,
    error: errorFetchingAsset,
    isLoading: isLoadingAsset,
  } = useQuery(['asset', fileIdParam], () => getAsset(), {
    enabled: !!fileIdParam && !!project?.code,
    cacheTime: 0,
    select: (res) => {
      try {
        const parsedDescription = res.data?.assetDescription ? JSON.parse(res.data.assetDescription) : {};
        return { ...res.data, assetDescription: parsedDescription };
      } catch (error) {
        return res.data;
      }
    },
  });

  const {
    data: assets,
    error: errorFetchingAssets,
    isLoading,
  } = useQuery(['assets', project?.code, project?.permission, currentPage, pageSize, sortDirection, assetTypeFilter, _debouncedFilterAssetDescription, isSearchTriggered], () => fetchAssets(), {
    enabled: !!project && !!project?.code && !fileIdParam,
    cacheTime: 0,
    select: (assetsResponse) => {
      const transformedAssets = assetsResponse.data.map((asset) => {
        try {
          const parsedDescription = asset.assetDescription ? JSON.parse(asset.assetDescription) : {};
          return { ...asset, assetDescription: parsedDescription };
        } catch (error) {
          return asset;
        }
      });
      return { ...assetsResponse, data: transformedAssets };
    },
  });

  const fetchAssetTypes = async () => GraphqlService.getAssetTypes({
    projectId: project?.code,
  });

  const {
    data: assetTypes,
    error: errorFetchingAssetTypes,
    isLoading: isLoadingAssetTypes,
  } = useQuery(['assetTypes', project?.code, project?.permission, currentPage, pageSize, sortDirection], () => fetchAssetTypes({
    projectId: project?.code,
  }), {
    enabled: !!project && isFeatureAvailable('assetTypes'),
    cacheTime: 0,
  });

  // Trigger automatic search on initial page load
  useEffect(() => {
    if (!hasInitializedSearch && project?.code) {
      setIsSearchTriggered(true);
      setHasInitializedSearch(true);
    }
  }, [project?.code, hasInitializedSearch]);

  useEffect(() => {
    if (errorFetchingAssetTypes && isFeatureAvailable('assetTypes')) {
      openSnackBar('Error fetching Asset Types', 'error');
    }
  }, [errorFetchingAssetTypes]);

  // fetch the assets when loading the page
  useEffect(() => {
    if (project?.code) {
      setSelectedAssets([]);
    }
  }, []);

  const handleSearchClick = () => {
    if (filterByAssetDescriptionValue) {
      setDebouncedFilterAssetDescription(filterByAssetDescriptionValue);
    } else {
      setDebouncedFilterAssetDescription('');
    }
    setIsSearchTriggered(true);
  };

  const handleFilterChipDelete = (filterName) => {
    if (filterName === 'description') {
      setFilterByAssetDescriptionValue('');
      setDebouncedFilterAssetDescription('');
    } else if (filterName === 'assetTypes') {
      setSelectedAssetTypes([]);
      setAssetTypeFilter(null);
    } else if (filterName === 'sortDirection') {
      setSortDirection('DESC');
    }
    setIsSearchTriggered(true);
  };

  const classes = useStyles();

  const queryClient = useQueryClient();

  const deleteAssetsMutation = useMutation(
    (deleteAssetInput) => GraphqlService.deleteAsset(project.code, deleteAssetInput),
    {
      onSuccess: (responsesArray) => {
        const showPopup = responsesArray.find((r) => r.statusCode !== null);
        if (showPopup) {
          const messages = [];
          responsesArray.map((r) => {
            messages.push(r.message);
            return 0;
          });
          openSnackBar(messages.join('\n'), 'error');
        } else {
          openSnackBar('Assets deleted!', 'success');
          setSelectedAssets([]);
          queryClient.invalidateQueries('assets');
          // If we're on the detail page, redirect to assets list after deletion
          if (fileIdParam) {
            history.push('/assets');
          }
        }
      },
      onError: (error) => {
        openSnackBar(`Error deleting asset: ${error.message}`, 'error');
      },
    },
  );

  const onClickNewAsset = () => {
    setAnalytics({
      event: 'button_clicked',
      extraParams: { name: 'asset_management-assets-add_assets' }
    });
    // If we're on the asset detail route, navigate to /assets/create instead of /assetDetail/:fileId/create
    if (match.url.startsWith('/assetDetail')) {
      history.push(NEW_ASSET_PATH);
    } else {
      history.push(`${match.url}/create`);
    }
  };

  const onClickNewDeployment = () => {
    // If we're on detail page and have assetFromParams, use it
    const assetsToDeploy = fileIdParam && assetFromParams
      ? [assetFromParams]
      : selectedAssets;

    if (assetsToDeploy.length <= 0) return;
    setSelectedAssets(assetsToDeploy);
    if (assetsToDeploy.length === 1) {
      setAnalytics({
        event: 'button_clicked',
        extraParams: { name: 'asset_management-assets-create_deploy' }
      });
      history.push('/assets/deployments/create/asset');
    }
    if (assetsToDeploy.length > 1) {
      setAnalytics({
        event: 'button_clicked',
        extraParams: { name: 'asset_management-assets-create_bundles' }
      });
      history.push('/bundles/create/asset');
    }
  };

  const onClickDeleteAssets = () => {
    // If we're on detail page and have assetFromParams, use it
    const assetsToDelete = fileIdParam && assetFromParams
      ? [assetFromParams]
      : selectedAssets;

    setYesNoDialogText(`You are about to delete ${assetsToDelete?.length} assets. Are you sure?`);
    setYesNoDialogOpen(true);
  };

  const confirmDeleteAssets = () => {
    setAnalytics({
      event: 'button_clicked',
      extraParams: { name: 'asset_management-assets-delete' }
    });
    // If we're on detail page and have assetFromParams, use it
    const assetsToDelete = fileIdParam && assetFromParams
      ? [assetFromParams]
      : selectedAssets;

    const deleteAssetInput = assetsToDelete.map((asset) => ({
      fileId: asset.fileID,
      chunks: asset.chunks,
    }));

    deleteAssetsMutation.mutate(deleteAssetInput);
  };

  const handlePageChange = (event, value) => {
    setCurrentPage(value - 1);
  };

  function renderContent() {
    const handleSelectAll = () => {
      if (selectedAssets.length === assets?.data?.length) {
        setSelectedAssets([]);
      } else {
        setSelectedAssets(assets?.data || []);
      }
    };

    const handleRowSelect = (asset) => {
      const selectedIndex = selectedAssets.findIndex((a) => a.fileID === asset.fileID);
      const newSelectedAssets = [...selectedAssets];
      if (selectedIndex === -1) {
        newSelectedAssets.push(asset);
      } else {
        newSelectedAssets.splice(selectedIndex, 1);
      }
      setSelectedAssets(newSelectedAssets);
    };

    const isRowSelected = (asset) => selectedAssets.some((a) => a.fileID === asset.fileID);

    const handleSelectChange = (event) => {
      setPageSize(event.target.value);
      setCurrentPage(0); // Reset to the first page when page size changes
    };

    const handleRowExpand = (event, index) => {
      event.stopPropagation();
      if (expandedRow !== index) {
        setAnalytics({
          event: 'button_clicked',
          extraParams: { name: 'asset_management-assets-expand' }
        })
      }
      setExpandedRow((prevExpandedRow) => (prevExpandedRow === index ? null : index));
    };

    const getAssetWithMetadataParsed = (asset) => {
      // delete metaData property and parse metaDataStringified as metaData
      const { metaDataStringified, ...assetWithoutMetadata } = asset;
      let parsedMetaData = {};
      try {
        parsedMetaData = metaDataStringified ? JSON.parse(metaDataStringified) : {};
      } catch (error) {
        parsedMetaData = metaDataStringified;
      }
      const assetWithMetadata = { ...assetWithoutMetadata, metaData: parsedMetaData };
      return assetWithMetadata;
    };

    if (errorFetchingAssets) {
      return <MessageCard message={`Error: ${errorFetchingAssets.message}`} />;
    }

    if (errorFetchingAsset) {
      return <MessageCard message={`Error: ${errorFetchingAsset.message}`} />;
    }

    if (assetFromParams) {
      return (
        <AssetDetail
          asset={assetFromParams}
          expandedProp={!!assetFromParams}
        />
      );
    }

    return (
      <>
        {(((isLoading || !project?.code) && !fileIdParam) || ((isLoadingAsset || !project?.code) && !!fileIdParam)) && (
          <ProgressDialog open header="Retrieving assets, please wait" />
        )}

        <div>
          <Typography component="div" className={classes.selectedItems}>
            {`${selectedAssets.length || 0} ${selectedAssets.length === 1 ? 'asset' : 'assets'} selected`}
          </Typography>
        </div>
        <TableContainer component={Paper} className={classes.table}>
          <Table aria-label="assets table" id="table-list-assets" size="small">
            <TableHead>
              <StyledTableRow>
                <StyledTableCell padding="checkbox">
                  <Checkbox
                    indeterminate={selectedAssets.length > 0 && selectedAssets.length < assets?.data?.length}
                    checked={selectedAssets.length === assets?.data?.length}
                    onChange={handleSelectAll}
                  />
                </StyledTableCell>
                <StyledTableCell>Type</StyledTableCell>
                <StyledTableCell>Version</StyledTableCell>
                <StyledTableCell>Creation Date</StyledTableCell>
                <StyledTableCell />
                <StyledTableCell />
              </StyledTableRow>
            </TableHead>
            <TableBody>
              {assets?.data?.map((asset, index) => (
                <React.Fragment key={asset.fileID}>
                  <StyledTableRow
                    selected={isRowSelected(asset)}
                    onClick={(event) => handleRowExpand(event, index)}
                    aria-checked={isRowSelected(asset)}
                    role="checkbox"
                    style={{ cursor: 'pointer' }}
                  >
                    <StyledTableCell padding="checkbox" onClick={(event) => event.stopPropagation()}>
                      <Checkbox
                        checked={isRowSelected(asset)}
                        onClick={(event) => {
                          event.stopPropagation();
                          handleRowSelect(asset);
                        }}
                      />
                    </StyledTableCell>
                    <StyledTableCell>{asset.type}</StyledTableCell>
                    <StyledTableCell>
                      {asset.assetDescription?.Version
                        ? `v.${asset.assetDescription.Version}`
                        : 'Not available'}
                    </StyledTableCell>
                    <StyledTableCell>{formatToPrintableDate(asset.dateTime)}</StyledTableCell>

                    <StyledTableCell>
                      {/* copy asset json with a button, align to the right */}
                      <Box display="flex" justifyContent="flex-end" width="100%">
                        <JsonPopup
                          title="Asset JSON"
                          jsonObject={typeof asset === 'object' && asset !== null ? getAssetWithMetadataParsed(asset) : {}}
                          onOpen={() => setAnalytics({
                            event: 'button_clicked',
                            extraParams: { name: 'asset_management-assets-json' }
                          })}
                        />
                      </Box>
                    </StyledTableCell>
                    <StyledTableCell>
                      <IconButton onClick={(event) => handleRowExpand(event, index)}>
                        {expandedRow === index ? <ExpandLess /> : <ExpandMore />}
                      </IconButton>
                    </StyledTableCell>
                  </StyledTableRow>
                  {expandedRow === index && (
                    <StyledTableRow>
                      <StyledTableCell colSpan={6}>
                        <AssetDetail asset={asset} />
                      </StyledTableCell>
                    </StyledTableRow>
                  )}

                </React.Fragment>
              ))}
            </TableBody>
          </Table>
          {assets?.pagination && (
            <PaginationComponent
              pageSize={pageSize}
              handleSelectChange={handleSelectChange}
              pagination={assets.pagination}
              handlePageChange={handlePageChange}
            />
          )}
        </TableContainer>

        {
          (!assets || !assets?.data || assets?.data?.length === 0) && !isLoading && !isLoadingAssetTypes
            ? <MessageCard message="No assets retrieved" /> : <></>
        }

      </>
    );
  }

  return (
    <Box>
      <YesNoDialog
        open={yesNoDialogOpen}
        setOpen={setYesNoDialogOpen}
        header="Delete asset"
        body={yesNoDialogText}
        action={() => confirmDeleteAssets()}
        actionName="Yes"
        id="confirmationDeleteAsset"
      />

      <PageBanner
        title="ASSETS"
        id="btnNewAsset"
        buttons={[
          'ADD ASSETS',
          (fileIdParam && assetFromParams ? 1 : selectedAssets.length) > 1 ? 'CREATE BUNDLE AND DEPLOY' : 'CREATE DEPLOYMENT',
          'DELETE ASSETS'
        ]}
        buttonIcons={[<AddIcon />, <UploadLogo />, <DeleteIcon />]}
        buttonDisabled={[
          false,
          (fileIdParam && assetFromParams ? 1 : selectedAssets.length) <= 0,
          (fileIdParam && assetFromParams ? 1 : selectedAssets.length) <= 0
        ]}
        onClicks={[onClickNewAsset, onClickNewDeployment, onClickDeleteAssets]}
        buttonsPermission={[CREATE_DEVICE_DEPLOY_PERMISSION, CREATE_DEVICE_DEPLOY_PERMISSION, DELETE_ASSETS_PERMISSION]}
      />

      <Grid
        className={classes.background}
        container
        spacing={0}
        direction="column"
        alignItems="stretch"
      >

        <AssetManagementFiltersBar
          title="Filters"
          projectCode={project?.code}
          onSearch={handleSearchClick}
          onFilterDelete={handleFilterChipDelete}
          filters={[
            {
              type: 'search',
              name: 'description',
              label: 'Asset Description',
              placeholder: 'Search by description',
              size: 'large',
            },
            ...(assetTypes?.data?.length > 0 && !isLoadingAssetTypes
              ? [
                  {
                    type: 'select',
                    name: 'assetTypes',
                    label: 'Asset Type',
                    options: assetTypes.data,
                    multiple: true,
                  },
                ]
              : []),
            {
              type: 'select',
              name: 'sortDirection',
              label: 'Sort by Date',
              options: [
                { value: 'ASC', label: 'Ascending' },
                { value: 'DESC', label: 'Descending' },
              ],
              size: 'small',
            },
          ]}
          activeFilters={{
            description: filterByAssetDescriptionValue,
            assetTypes: selectedAssetTypes,
            sortDirection,
          }}
          onFilterChange={(name, value) => {
            if (name === 'description') {
              setFilterByAssetDescriptionValue(value);
            } else if (name === 'assetTypes') {
              setSelectedAssetTypes(value);
              setAssetTypeFilter(value.join(',').toString());
              setCurrentPage(0);
            } else if (name === 'sortDirection') {
              setSortDirection(value);
            }
          }}
          onClearAll={() => {
            setFilterByAssetDescriptionValue('');
            setDebouncedFilterAssetDescription('');
            setSelectedAssetTypes([]);
            setAssetTypeFilter(null);
            setSortDirection('DESC');
            setCurrentPage(0);
            setIsSearchTriggered(true);
          }}
        />

        <div>
          {renderContent()}
        </div>
      </Grid>
    </Box>
  );
}
